import { useEffect, useRef, useState, type DragEvent } from "react";
import { ApiError, api, type MediaItem } from "../api";
import { Icon } from "../icons";
import { formatBytes, relativeTime } from "../format";
import { uploadMedia } from "../upload";
import { Banner, EmptyState, FilterChips, PageHeader, SearchField } from "../ui";

type Job = { id: string; name: string; ratio: number; error: string };
type Kind = "all" | "video" | "image";

export function MediaPage() {
  const [media, setMedia] = useState<MediaItem[] | null>(null);
  const [storageConfigured, setStorageConfigured] = useState(true);
  const [maxUploadBytes, setMaxUploadBytes] = useState(0);
  const [error, setError] = useState("");
  const [jobs, setJobs] = useState<Job[]>([]);
  const [preview, setPreview] = useState<MediaItem | null>(null);
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<Kind>("all");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [savingName, setSavingName] = useState(false);

  async function load() {
    const result = await api<{ media: MediaItem[]; storageConfigured: boolean; maxUploadBytes: number }>("/api/media");
    setMedia(result.media);
    setStorageConfigured(result.storageConfigured);
    setMaxUploadBytes(result.maxUploadBytes);
  }

  useEffect(() => {
    load().catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Chargement impossible"));
  }, []);

  useEffect(() => {
    if (!preview) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (renamingId === preview.id) {
        if (!savingName) {
          setRenamingId(null);
          setDraftName("");
        }
        return;
      }
      setPreview(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview, renamingId, savingName]);

  function startRename(item: MediaItem) {
    setError("");
    setRenamingId(item.id);
    setDraftName(item.originalName);
  }

  function stopRename() {
    if (savingName) return;
    setRenamingId(null);
    setDraftName("");
  }

  function closePreview() {
    if (renamingId === preview?.id) stopRename();
    setPreview(null);
  }

  async function saveRename(item: MediaItem) {
    const name = draftName.trim();
    if (!name || name.length > 180) {
      setError("Le nom doit contenir entre 1 et 180 caractères");
      return;
    }
    if (name === item.originalName) {
      setRenamingId(null);
      return;
    }
    setSavingName(true);
    setError("");
    try {
      const result = await api<{ media: MediaItem }>(`/api/media/${item.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      });
      const next = { ...result.media, previewUrl: result.media.previewUrl ?? item.previewUrl };
      setMedia((current) => current?.map((entry) => (entry.id === next.id ? next : entry)) ?? null);
      setPreview((current) => (current?.id === next.id ? { ...current, ...next, previewUrl: next.previewUrl ?? current.previewUrl } : current));
      setRenamingId(null);
      setDraftName("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Renommage impossible");
    } finally {
      setSavingName(false);
    }
  }

  async function onFiles(files: FileList | File[]) {
    const list = [...files];
    if (list.length === 0) return;
    setError("");
    for (const file of list) {
      const jobId = crypto.randomUUID();
      setJobs((current) => [...current, { id: jobId, name: file.name, ratio: 0, error: "" }]);
      try {
        if (maxUploadBytes && file.size > maxUploadBytes) {
          throw new ApiError(`Fichier trop lourd (maximum ${formatBytes(maxUploadBytes)})`, 413);
        }
        await uploadMedia(file, (ratio) => {
          setJobs((current) => current.map((job) => (job.id === jobId ? { ...job, ratio } : job)));
        });
        setJobs((current) => current.map((job) => (job.id === jobId ? { ...job, ratio: 1 } : job)));
      } catch (reason) {
        const text = reason instanceof Error ? reason.message : "Échec de l'envoi";
        setJobs((current) => current.map((job) => (job.id === jobId ? { ...job, error: text } : job)));
        setError(text);
      }
    }
    await load();
  }

  const ready = (media ?? []).filter((item) => item.status === "ready");
  const pending = (media ?? []).filter((item) => item.status === "pending");
  const needle = query.trim().toLowerCase();
  const visible = ready.filter((item) => {
    if (kind !== "all" && item.type !== kind) return false;
    if (!needle) return true;
    return item.originalName.toLowerCase().includes(needle);
  });

  return (
    <section>
      <PageHeader
        kicker="Bibliothèque"
        title="Médias"
        lede="Déposez une vidéo ou une image. Le fichier est prêt pour une playlist dès que l'envoi est fini."
        action={media ? <p className="stat-inline"><strong>{ready.length}</strong> {ready.length === 1 ? "prêt" : "prêts"}</p> : null}
      />
      {!storageConfigured ? (
        <Banner tone="error">Mega S4 n'est pas configuré. Les envois resteront impossibles tant que l'endpoint, le bucket et les clés ne sont pas renseignés.</Banner>
      ) : null}
      {error ? <Banner tone="error">{error}</Banner> : null}
      <Dropzone disabled={!storageConfigured} onFiles={(files) => void onFiles(files)} />
      {jobs.length > 0 ? (
        <ul className="jobs">
          {jobs.map((job) => (
            <li key={job.id}>
              <span>{job.name}</span>
              {job.error ? <em className="bad">{job.error}</em> : <em>{Math.round(job.ratio * 100)} %</em>}
              <progress value={job.error ? 0 : job.ratio} max={1} />
            </li>
          ))}
        </ul>
      ) : null}
      {ready.length > 0 ? (
        <div className="toolbar">
          <SearchField value={query} onChange={setQuery} placeholder="Nom du fichier" />
          <FilterChips
            label="Type de média"
            value={kind}
            onChange={setKind}
            options={[
              { id: "all", label: "Tout" },
              { id: "video", label: "Vidéos" },
              { id: "image", label: "Images" },
            ]}
          />
        </div>
      ) : null}
      <div className="media-grid">
        {visible.map((item) => {
          const editing = renamingId === item.id && preview?.id !== item.id;
          const details = `${formatBytes(item.size)} · ${relativeTime(item.uploadedAt)}`;
          return (
            <article key={item.id} className="media-card">
              <div className="preview">
                <span className={`type-badge ${item.type}`}>{item.type === "video" ? "Vidéo" : "Image"}</span>
                <MediaPreview item={item} />
                <button type="button" className="preview-hit" aria-label={`Aperçu de ${item.originalName}`} onClick={() => setPreview(item)} />
              </div>
              {editing ? (
                <RenameForm
                  name={draftName}
                  saving={savingName}
                  details={details}
                  onChange={setDraftName}
                  onCancel={stopRename}
                  onSubmit={() => void saveRename(item)}
                />
              ) : (
                <>
                  <h2>{item.originalName}</h2>
                  <div className="media-card-foot">
                    <p className="muted">{details}</p>
                    <div className="media-actions">
                      <button type="button" onClick={() => startRename(item)}>Renommer</button>
                      <button type="button" className="danger" onClick={() => void remove(item, load, setError)}>Supprimer</button>
                    </div>
                  </div>
                </>
              )}
            </article>
          );
        })}
      </div>
      {media && ready.length === 0 ? (
        <EmptyState icon="media" title="La bibliothèque est vide" text="Déposez un MP4, un JPEG, un PNG, un WebP ou un GIF dans la zone ci-dessus." />
      ) : null}
      {ready.length > 0 && visible.length === 0 ? <p className="quiet-empty">Aucun média pour cette recherche.</p> : null}
      {pending.length > 0 ? (
        <div className="card block">
          <h2>Envois incomplets</h2>
          <p className="muted">Ces fichiers n'ont pas fini d'arriver. Vous pouvez les abandonner.</p>
          <ul className="compact-list pair">
            {pending.map((item) => (
              <li key={item.id}>
                <span>{item.originalName}</span>
                <button type="button" onClick={() => void abort(item.id, load, setError)}>Annuler</button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {preview ? (
        <div className="modal" role="dialog" aria-modal="true" aria-label="Aperçu" onClick={closePreview}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="modal-close" onClick={closePreview}>Fermer</button>
            {preview.type === "image" && preview.previewUrl ? <img src={preview.previewUrl} alt={preview.originalName} /> : null}
            {preview.type === "video" && preview.previewUrl ? <video src={preview.previewUrl} controls autoPlay /> : null}
            {renamingId === preview.id ? (
              <RenameForm
                name={draftName}
                saving={savingName}
                onChange={setDraftName}
                onCancel={stopRename}
                onSubmit={() => void saveRename(preview)}
              />
            ) : (
              <>
                <p>{preview.originalName}</p>
                <button type="button" onClick={() => startRename(preview)}>Renommer</button>
              </>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function RenameForm({
  name,
  saving,
  details,
  onChange,
  onCancel,
  onSubmit,
}: {
  name: string;
  saving: boolean;
  details?: string;
  onChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <form
      className="rename-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <label>
        Nom
        <input
          value={name}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              onCancel();
            }
          }}
          maxLength={180}
          required
          autoFocus
          autoComplete="off"
          spellCheck
          disabled={saving}
          aria-label="Nouveau nom du fichier"
        />
      </label>
      {details ? <p className="muted">{details}</p> : null}
      <div className="media-actions">
        <button type="submit" className="primary" disabled={saving}>Enregistrer</button>
        <button type="button" onClick={onCancel} disabled={saving}>Annuler</button>
      </div>
    </form>
  );
}

function MediaPreview({ item }: { item: MediaItem }) {
  const [failed, setFailed] = useState(false);
  if (!item.previewUrl || failed) {
    return <span className="file-badge">{item.type === "video" ? "MP4" : "IMG"}</span>;
  }
  if (item.type === "image") {
    return <img src={item.previewUrl} alt="" onError={() => setFailed(true)} />;
  }
  return <VideoStill url={item.previewUrl} onError={() => setFailed(true)} />;
}

function VideoStill({ url, onError }: { url: string; onError: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const showFrame = () => {
      const duration = video.duration;
      if (!Number.isFinite(duration) || duration < 0.3) return;
      const at = Math.min(1, Math.max(0.5, duration * 0.1));
      if (video.currentTime >= 0.2) return;
      try {
        video.currentTime = at;
      } catch {
        onErrorRef.current();
      }
    };
    video.addEventListener("loadeddata", showFrame);
    if (video.readyState >= 2) showFrame();
    return () => video.removeEventListener("loadeddata", showFrame);
  }, [url]);

  return (
    <video
      ref={videoRef}
      src={url}
      muted
      playsInline
      preload="metadata"
      aria-hidden="true"
      onError={() => onErrorRef.current()}
    />
  );
}

function Dropzone({ disabled, onFiles }: { disabled: boolean; onFiles: (files: FileList) => void }) {
  const [over, setOver] = useState(false);
  function drop(event: DragEvent) {
    event.preventDefault();
    setOver(false);
    if (event.dataTransfer.files.length) onFiles(event.dataTransfer.files);
  }
  return (
    <label
      className={`dropzone ${over ? "over" : ""} ${disabled ? "disabled" : ""}`}
      onDragOver={(event) => { event.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
    >
      <input
        type="file"
        accept="video/mp4,image/jpeg,image/png,image/webp,image/gif"
        multiple
        disabled={disabled}
        onChange={(event) => {
          if (event.target.files) onFiles(event.target.files);
          event.target.value = "";
        }}
      />
      <span className="drop-icon"><Icon name="upload" /></span>
      <strong>Ajouter des fichiers</strong>
      <span>Glissez une vidéo MP4 ou une image, ou cliquez pour parcourir.</span>
      <span className="format-row">
        <span>MP4</span>
        <span>JPEG</span>
        <span>PNG</span>
        <span>WebP</span>
        <span>GIF</span>
      </span>
    </label>
  );
}

async function remove(item: MediaItem, load: () => Promise<void>, setError: (text: string) => void) {
  if (!window.confirm(`Supprimer ${item.originalName} ? Il sera retiré des playlists.`)) return;
  try {
    await api(`/api/media/${item.id}`, { method: "DELETE" });
    await load();
  } catch (reason) {
    setError(reason instanceof Error ? reason.message : "Suppression impossible");
  }
}

async function abort(id: string, load: () => Promise<void>, setError: (text: string) => void) {
  try {
    await api(`/api/media/uploads/${id}`, { method: "DELETE" });
    await load();
  } catch (reason) {
    setError(reason instanceof Error ? reason.message : "Annulation impossible");
  }
}
